import { useState } from 'react';
import * as R from 'remeda';
import { FormattedMessage } from 'react-intl';
import { Queue as QueueType } from '@rossum/api-client/queues';
import { IconBan, IconCheck, IconChevronDown } from '@rossum/ui/icons/tabler';
import {
  Button,
  Chip,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemText,
  SvgIcon,
} from '@rossum/ui/material';
import { Url } from '../../../types/basic';
import { User } from '../../../types/user';
import { Queue } from './Queue';
import { ToggleAllDialogAction } from './ToggleAccessToAllQueuesDialog';

type Props = {
  activeQueuesCount: number;
  isFirst: boolean;
  name: string;
  onToggleAllClick: (action: ToggleAllDialogAction) => void;
  queues: Array<QueueType>;
  updateUserDetail: (_user: Partial<User>) => void;
  userQueues: Array<Url>;
  forceExpand: boolean;
};

export const Workspace = ({
  activeQueuesCount,
  isFirst,
  name,
  onToggleAllClick,
  queues,
  updateUserDetail,
  userQueues,
  forceExpand,
}: Props) => {
  const isQueueActive = (queueUrl: string) => userQueues.includes(queueUrl);
  const [expanded, setExpanded] = useState(isFirst);

  const toggleQueueActive = (queueUrl: string) =>
    isQueueActive(queueUrl)
      ? updateUserDetail({ queues: userQueues.filter(url => url !== queueUrl) })
      : updateUserDetail({ queues: [...userQueues, queueUrl] });

  const hoverButtonClass = !expanded ? 'hover-button' : undefined;

  return (
    <>
      <ListItem
        disablePadding
        onClick={() => setExpanded(!expanded)}
        secondaryAction={
          <>
            <Button
              data-cy="enable-all-queues"
              size="small"
              color="secondary"
              startIcon={
                <SvgIcon fontSize="large">
                  <IconCheck />
                </SvgIcon>
              }
              onClick={e => {
                e.stopPropagation();
                onToggleAllClick('addAllQueues');
              }}
              className={hoverButtonClass}
            >
              <FormattedMessage
                id="containers.users.components.workspace.enableAll"
                values={{
                  count: forceExpand ? queues.length : 0,
                }}
              />
            </Button>
            <Button
              data-cy="disable-all-queues"
              size="small"
              color="secondary"
              startIcon={
                <SvgIcon fontSize="large">
                  <IconBan />
                </SvgIcon>
              }
              onClick={e => {
                e.stopPropagation();
                onToggleAllClick('disableAllQueues');
              }}
              className={hoverButtonClass}
            >
              <FormattedMessage
                id="containers.users.components.workspace.disableAll"
                values={{
                  count: forceExpand ? queues.length : 0,
                }}
              />
            </Button>
          </>
        }
        sx={{
          '.hover-button': {
            opacity: expanded ? 1 : 0,
            transition: 'opacity 0.4s',
          },

          '&:hover .hover-button': {
            opacity: 1,
          },
          cursor: 'pointer',
        }}
      >
        <IconButton>
          <SvgIcon
            color="primary"
            fontSize="medium"
            sx={{
              pr: 0.2,
              transform: `rotate(${expanded ? 0 : -90}deg)`,
            }}
          >
            <IconChevronDown />
          </SvgIcon>
        </IconButton>
        <ListItemText
          primary={name}
          sx={{
            flex: '0 0 auto',
          }}
        />

        <Chip
          sx={{ ml: 1 }}
          color={
            activeQueuesCount === null
              ? queues.length === 0
                ? 'default'
                : 'info'
              : activeQueuesCount === 0
                ? 'default'
                : 'info'
          }
          label={
            R.isNumber(activeQueuesCount)
              ? `${activeQueuesCount} / ${queues.length}`
              : `${queues.length}`
          }
        />
      </ListItem>
      <Collapse
        in={expanded}
        timeout={400}
        appear
        mountOnEnter
        unmountOnExit
        sx={{ width: '100%' }}
      >
        <List>
          {queues.map(queue => (
            <Queue
              key={queue.id}
              queue={queue}
              active={isQueueActive(queue.url)}
              onToggle={() => toggleQueueActive(queue.url)}
            />
          ))}
        </List>
      </Collapse>
    </>
  );
};
